import { isStripType, unitLabelOf } from "@/lib/medTypes";

import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PackagePlus, Loader2, Search } from "lucide-react";
import { toast } from "sonner";

export default function AddStock() {
  const [medicines, setMedicines] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [entries, setEntries] = useState([]);
  const [medQuery, setMedQuery] = useState("");
  const [selectedMed, setSelectedMed] = useState(null);
  const [supplierId, setSupplierId] = useState("none");
  const [addStrips, setAddStrips] = useState("");
  const [addLoose, setAddLoose] = useState("");
  const [purchasePrice, setPurchasePrice] = useState("");
  const [batchNo, setBatchNo] = useState("");
  const [expiry, setExpiry] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const loadEntries = () => api.get("/stock/entries").then(({ data }) => setEntries(data));

  useEffect(() => {
    api.get("/medicines").then(({ data }) => setMedicines(data));
    api.get("/suppliers").then(({ data }) => setSuppliers(data));
    loadEntries();
  }, []);

  const filtered = medQuery.trim()
    ? medicines.filter((m) => m.name.toLowerCase().includes(medQuery.toLowerCase())).slice(0, 8)
    : [];

  const submit = async () => {
    if (!selectedMed) return toast.error("Select a medicine");
    if ((Number(addStrips) || 0) <= 0 && (Number(addLoose) || 0) <= 0) return toast.error("Add at least 1 unit");
    setSaving(true);
    try {
      await api.post("/stock/entries", {
        medicine_id: selectedMed.id,
        supplier_id: supplierId === "none" ? "" : supplierId,
        add_strips: Number(addStrips) || 0,
        add_loose: Number(addLoose) || 0,
        purchase_price: purchasePrice === "" ? null : Number(purchasePrice),
        batch_no: batchNo || null,
        expiry_date: expiry || null,
        note,
      });
      toast.success(`Stock added: ${selectedMed.name}`);
      const { data } = await api.get("/medicines");
      setMedicines(data);
      const updated = data.find((m) => m.id === selectedMed.id);
      setSelectedMed(updated || null);
      setAddStrips(""); setAddLoose(""); setNote("");
      loadEntries();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="add-stock-page">
      <div className="space-y-4">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Add Stock</h1>
          <p className="text-sm text-slate-500">Add newly purchased stock into inventory.</p>
        </div>

        <Card className="p-5 border-slate-200 space-y-4">
          <div>
            <Label className="text-xs">Medicine *</Label>
            {selectedMed ? (
              <div className="mt-1.5 p-3 rounded-lg border border-emerald-200 bg-emerald-50 flex items-center justify-between" data-testid="selected-medicine">
                <div>
                  <div className="font-semibold text-emerald-900">{selectedMed.name}</div>
                  <div className="text-xs text-emerald-700">Current: {selectedMed.stock_strips} {unitLabelOf(selectedMed).toLowerCase()}s{isStripType(selectedMed.med_type) ? ` + ${selectedMed.stock_loose_tablets} loose` : ""}</div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => setSelectedMed(null)}>Change</Button>
              </div>
            ) : (
              <>
                <div className="relative mt-1.5">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input data-testid="stock-med-search" value={medQuery} onChange={(e) => setMedQuery(e.target.value)} placeholder="Search medicine…" className="pl-9 h-11" />
                </div>
                {filtered.length > 0 && (
                  <div className="mt-1 border border-slate-200 rounded-lg divide-y divide-slate-100 bg-white max-h-56 overflow-y-auto">
                    {filtered.map((m) => (
                      <button key={m.id} data-testid={`stock-med-option-${m.id}`} onClick={() => { setSelectedMed(m); setMedQuery(""); }} className="w-full text-left px-3 py-2.5 hover:bg-emerald-50 text-sm">
                        <div className="font-medium text-slate-900">{m.name}</div>
                        <div className="text-xs text-slate-500">Stock: {m.stock_strips} {unitLabelOf(m).toLowerCase()}s • Batch {m.batch_no || "—"}</div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div>
            <Label className="text-xs">Supplier (optional)</Label>
            <Select value={supplierId} onValueChange={setSupplierId}>
              <SelectTrigger data-testid="stock-supplier-select" className="mt-1.5 h-11"><SelectValue placeholder="Select supplier" /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="none">— No supplier —</SelectItem>
                {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Add Strips</Label>
              <Input data-testid="stock-strips-input" type="number" min="0" value={addStrips} onChange={(e) => setAddStrips(e.target.value)} className="mt-1.5 h-11" placeholder="0" />
            </div>
            <div>
              <Label className="text-xs">Add Loose Tablets</Label>
              <Input data-testid="stock-loose-input" type="number" min="0" value={addLoose} onChange={(e) => setAddLoose(e.target.value)} className="mt-1.5 h-11" placeholder="0" />
            </div>
            <div>
              <Label className="text-xs">Purchase Price (₹/strip)</Label>
              <Input type="number" min="0" value={purchasePrice} onChange={(e) => setPurchasePrice(e.target.value)} className="mt-1.5 h-11" placeholder={selectedMed ? String(selectedMed.purchase_price) : "0.00"} />
            </div>
            <div>
              <Label className="text-xs">New Batch No.</Label>
              <Input value={batchNo} onChange={(e) => setBatchNo(e.target.value)} className="mt-1.5 h-11" placeholder={selectedMed?.batch_no || "Optional"} />
            </div>
            <div>
              <Label className="text-xs">New Expiry (YYYY-MM-DD)</Label>
              <Input value={expiry} onChange={(e) => setExpiry(e.target.value)} className="mt-1.5 h-11" placeholder={selectedMed?.expiry_date || "Optional"} />
            </div>
            <div>
              <Label className="text-xs">Note</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1.5 h-11" placeholder="Optional" />
            </div>
          </div>

          <Button onClick={submit} disabled={saving || !selectedMed} data-testid="stock-submit-btn" className="w-full h-11 bg-emerald-900 hover:bg-emerald-800">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <PackagePlus className="w-4 h-4 mr-2" />}
            Add Stock to Inventory
          </Button>
        </Card>
      </div>

      <div className="space-y-3">
        <h2 className="font-display text-lg font-bold text-slate-900">Recent Stock Entries</h2>
        <Card className="border-slate-200">
          <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
            <Table data-testid="stock-entries-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Medicine</TableHead>
                  <TableHead>Added</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell>
                      <div className="font-medium text-sm">{e.medicine_name}</div>
                      <div className="text-xs text-slate-500">Batch {e.batch_no || "—"}</div>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 font-mono-num">+{e.add_strips} units</Badge>
                      {e.add_loose > 0 && <div className="text-xs text-slate-500 mt-0.5">+{e.add_loose} loose</div>}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{e.supplier_name || "—"}</TableCell>
                    <TableCell className="text-xs text-slate-500">{new Date(e.created_at).toLocaleDateString("en-IN")}</TableCell>
                  </TableRow>
                ))}
                {entries.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-8 text-slate-400">No stock entries yet</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
    </div>
  );
}
